import React from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import Footer from '../components/Footer';
import BottomNavBar from '../components/BottomNavBar';

const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-xl font-bold text-on-surface mb-3">{title}</h2>
    <div className="space-y-3 text-on-surface-variant leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-surface pb-24 md:pb-0">
      <TopAppBar />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/" className="text-sm text-outline hover:text-on-surface flex items-center gap-1 mb-6">
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-on-surface mb-2">Privacy Policy</h1>
          <p className="text-sm text-outline">Last updated: 4 June 2026 · Effective: 4 June 2026</p>
        </div>

        <div className="bg-secondary/10 rounded-xl p-5 mb-10 text-sm text-on-surface-variant">
          <p>
            This policy applies to Buildon and describes how we collect, use, store, and protect your personal
            information. It is issued in compliance with the{' '}
            <strong>Protection of Personal Information Act 4 of 2013 (POPIA)</strong> of South Africa.
            Please read it carefully before using our platform.
          </p>
        </div>

        <Section title="1. Who we are and how to contact us">
          <p>
            <strong>Responsible party:</strong> Buildon Professional Services (Pty) Ltd<br />
            <strong>Information Officer:</strong> [NAME TBC]<br />
            <strong>Email:</strong> privacy@buildon.co.za<br />
            <strong>Address:</strong> [ADDRESS TBC], Cape Town, South Africa
          </p>
          <p>
            For any privacy-related queries, requests, or complaints, contact our Information Officer at the
            address above. We will respond within 10 business days.
          </p>
        </Section>

        <Section title="2. What personal information we collect">
          <p>We collect personal information in the following categories:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Account information:</strong> email address, password (stored as a secure hash), and
              account type (client or contractor).
            </li>
            <li>
              <strong>Identity information:</strong> first name, last name, and profile photograph.
            </li>
            <li>
              <strong>Contractor profile information:</strong> company name, phone number, business address,
              city, province, postal code, biography, website, years of experience, service categories,
              service areas, and tier.
            </li>
            <li>
              <strong>Compliance documents:</strong> credential documents you upload (licence certificates,
              insurance certificates, NHBRC registration, BBBEE certificates, health and safety documentation)
              and associated registration numbers.
            </li>
            <li>
              <strong>Job and transaction information:</strong> job postings, job descriptions, photographs
              attached to jobs, and the status of jobs you create or respond to.
            </li>
            <li>
              <strong>Communications:</strong> messages sent between clients and contractors on the platform.
            </li>
            <li>
              <strong>Reviews:</strong> ratings and reviews submitted about contractors.
            </li>
            <li>
              <strong>Technical information:</strong> your authentication session token, stored locally in
              your browser.
            </li>
          </ul>
        </Section>

        <Section title="3. Why we collect your information and our lawful basis">
          <p>We process your personal information for the following purposes, each with a corresponding lawful basis under POPIA:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-outline-variant rounded-lg overflow-hidden">
              <thead className="bg-surface-container">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-on-surface">Purpose</th>
                  <th className="text-left px-4 py-3 font-semibold text-on-surface">Lawful basis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant">
                {[
                  ['Creating and managing your account', 'Performance of a contract / consent'],
                  ['Enabling clients to find and contact contractors', 'Performance of a contract'],
                  ['Processing job postings and responses', 'Performance of a contract'],
                  ['Verifying contractor credentials (admin review)', 'Legitimate interest / legal obligation'],
                  ['Facilitating messaging between users', 'Performance of a contract'],
                  ['Displaying contractor profiles and reviews', 'Performance of a contract / legitimate interest'],
                  ['Improving the platform and diagnosing technical issues', 'Legitimate interest'],
                  ['Complying with legal obligations', 'Legal obligation'],
                ].map(([purpose, basis]) => (
                  <tr key={purpose} className="bg-surface">
                    <td className="px-4 py-3">{purpose}</td>
                    <td className="px-4 py-3 text-outline">{basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4. Who we share your information with">
          <p>We do not sell your personal information. We may share it with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Other platform users:</strong> your contractor profile, including name, photo, location,
              categories, and credentials, is visible to clients on the platform. Your messages are visible
              to the recipient only.
            </li>
            <li>
              <strong>Service providers:</strong> third-party providers that host our infrastructure, store
              uploaded files, and support platform operations. These providers are contractually required to
              protect your information and may not use it for their own purposes.
            </li>
            <li>
              <strong>Law enforcement or regulators:</strong> where required by law, court order, or to
              protect the rights, property, or safety of Buildon or others.
            </li>
          </ul>
          <p>
            We do not transfer your personal information outside South Africa unless appropriate safeguards
            are in place as required by POPIA section 72.
          </p>
        </Section>

        <Section title="5. How long we keep your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>Account and profile information is retained for as long as your account is active.</li>
            <li>
              After you delete your account, we retain certain information for up to 5 years where required
              by financial, tax, or legal obligations.
            </li>
            <li>Messages are retained for the duration of the associated job, then for 12 months.</li>
            <li>Uploaded credential documents are retained until you delete them or close your account.</li>
            <li>Technical logs are retained for up to 90 days.</li>
          </ul>
        </Section>

        <Section title="6. How we protect your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>Passwords are stored as cryptographic hashes and are never stored in plain text.</li>
            <li>All data in transit is protected using TLS encryption.</li>
            <li>Access to personal data is restricted to authorised personnel only.</li>
            <li>
              Your session token is stored in your browser's local storage. You can remove it at any time
              by logging out.
            </li>
          </ul>
          <p>
            Despite these measures, no system is completely secure. If you suspect unauthorised access to
            your account, contact us immediately at privacy@buildon.co.za.
          </p>
        </Section>

        <Section id="your-rights" title="7. Your rights under POPIA">
          <p>As a data subject, you have the following rights:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Right of access:</strong> request a copy of the personal information we hold about you.
            </li>
            <li>
              <strong>Right to correction:</strong> request that inaccurate or incomplete information be
              corrected. You can update most information directly in your profile settings.
            </li>
            <li>
              <strong>Right to deletion:</strong> request that we delete your personal information. You can
              delete your account from your profile settings. Where we are legally required to retain certain
              information, we will inform you.
            </li>
            <li>
              <strong>Right to object:</strong> object to the processing of your personal information where
              it is based on legitimate interest.
            </li>
            <li>
              <strong>Right to lodge a complaint:</strong> if you believe we have not handled your personal
              information lawfully, you may lodge a complaint with the{' '}
              <strong>Information Regulator of South Africa</strong>:
              <br />
              Website: <span className="text-primary">inforegulator.org.za</span>
              <br />
              Email: complaints.IR@justice.gov.za
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact our Information Officer at{' '}
            <strong>privacy@buildon.co.za</strong>. We will respond within 10 business days.
          </p>
        </Section>

        <Section id="cookies" title="8. Cookies and local storage">
          <p>
            Buildon does not use tracking or advertising cookies. We store a single authentication token
            in your browser's local storage to keep you signed in. This token expires automatically and
            is removed when you log out.
          </p>
          <p>
            We do not use analytics, advertising, or third-party cookies. If this changes, we will update
            this policy and notify you before any new tracking begins.
          </p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be communicated via a notice
            on the platform or by email before they take effect. The date at the top of this page shows when
            it was last updated. Continued use of the platform after changes are notified constitutes
            acceptance of the updated policy.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-outline-variant text-sm text-outline">
          <p>
            Questions? Contact our Information Officer at{' '}
            <strong>privacy@buildon.co.za</strong>.
          </p>
          <p className="mt-2">
            View our{' '}
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.
          </p>
        </div>
      </main>
      <Footer />
      <BottomNavBar />
    </div>
  );
};

export default PrivacyPolicyPage;
